import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { AdminProduct, AdminSubscription, AdminTransaction, StripeLogItem } from '../../api/adminTypes'
import { formatDateTime } from '../trading/tradingConstants'
import { AdminNotice, AdminTabs, ConfirmButton, EmailLookupForm } from './adminShared'
import { describeAdminError, formatMoney, redactedJson, useAdminActions, useEmailParam } from './adminHelpers'
type Tab = 'subscriptions' | 'products' | 'transactions' | 'stripe'

/**
 * Equivalent of legacy `AdminPaymentUserComponent` (route
 * `admin/payment/user`) — one user's full payment record (subs, products,
 * transactions, Stripe client logs), per the audit. BUSINESS-CRITICAL:
 * product modify (#5) and payment cancel (#6) both mutate real payment
 * state for a user who is NOT the admin.
 *  - Marking a product paid vs. DELETING it are two distinct, separately
 *    confirmed actions (contract: `payed: false` deletes the product — no
 *    ambiguous single checkbox that could delete on an unnoticed uncheck).
 *  - Cancel drops the payment method and cancels every active subscription;
 *    the contract calls it "irreversible-looking" — confirmed inline
 *    (`ConfirmButton`) and disabled with no e-mail (contract: "no e-mail
 *    given -> nothing happens", which reads as silent success otherwise).
 */
export function AdminPaymentUserPage() {
  const [email, setEmail] = useEmailParam()
  const [tab, setTab] = useState<Tab>('subscriptions')

  const [subscriptions, setSubscriptions] = useState<AdminSubscription[] | null>(null)
  const [products, setProducts] = useState<AdminProduct[] | null>(null)
  const [transactions, setTransactions] = useState<AdminTransaction[] | null>(null)
  const [stripeItems, setStripeItems] = useState<StripeLogItem[]>([])
  const [stripeCursor, setStripeCursor] = useState<string | null>(null)
  const [stripeFinished, setStripeFinished] = useState(false)
  const [loadFeedback, setLoadFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  const { isBusy, feedbackFor, run, setFeedback } = useAdminActions()
  const [payedAtDrafts, setPayedAtDrafts] = useState<Record<string, string>>({})
  const [altMethodDrafts, setAltMethodDrafts] = useState<Record<string, boolean>>({})

  // `fetchAll` re-fetches WITHOUT clearing the current data first — used
  // after a product/cancel mutation so the tab an admin is looking at (and
  // the feedback that mutation just set) does not flash to "Loading…" and
  // back, and so a cached DOM reference in a test/click sequence never goes
  // stale mid-interaction. `loadAll` (email change / initial mount) resets
  // first, since there is nothing worth keeping on screen yet.
  const fetchAll = useCallback((targetEmail: string) => {
    setLoadFeedback(undefined)
    adminClient.subscriptions(targetEmail).then(setSubscriptions).catch((err) => setLoadFeedback(describeAdminError(err)))
    adminClient.products(targetEmail).then(setProducts).catch((err) => setLoadFeedback(describeAdminError(err)))
    adminClient.transactions(targetEmail).then(setTransactions).catch((err) => setLoadFeedback(describeAdminError(err)))
    adminClient
      .stripeLogs(targetEmail)
      .then((page) => {
        setStripeItems(page.items)
        setStripeCursor(page.cursor)
        setStripeFinished(!!page.finished)
      })
      .catch((err) => setLoadFeedback(describeAdminError(err)))
  }, [])
  const loadAll = useCallback(
    (targetEmail: string) => {
      setSubscriptions(null)
      setProducts(null)
      setTransactions(null)
      setStripeItems([])
      setStripeCursor(null)
      setStripeFinished(false)
      fetchAll(targetEmail)
    },
    [fetchAll],
  )

  useEffect(() => {
    if (email) loadAll(email)
  }, [email, loadAll])

  function loadMoreStripe() {
    if (!email || !stripeCursor) return
    adminClient.stripeLogs(email, stripeCursor).then((page) => {
      setStripeItems((prev) => [...prev, ...page.items])
      setStripeCursor(page.cursor)
      setStripeFinished(!!page.finished)
    })
  }

  function markPaid(product: AdminProduct) {
    if (!email || !product.product) return
    const key = `modify-${product.product}`
    void run(
      key,
      () =>
        adminClient.modifyProduct(product.product!, email, {
          payed: true,
          alternativeMethod: altMethodDrafts[product.product!] ?? !!product.alternativeMethod,
          payedAt: payedAtDrafts[product.product!] || undefined,
        }),
      () => {
        setFeedback(key, { tone: 'success', text: 'Product marked as paid.' })
        fetchAll(email)
      },
    )
  }

  function deleteProduct(product: AdminProduct) {
    if (!email || !product.product) return
    const key = `modify-${product.product}`
    void run(
      key,
      () => adminClient.modifyProduct(product.product!, email, { payed: false }),
      () => {
        setFeedback(key, { tone: 'success', text: 'Product deleted.' })
        fetchAll(email)
      },
    )
  }

  function cancelPayment() {
    if (!email) return
    void run(
      'cancel',
      () => adminClient.cancelPayment(email),
      () => {
        setFeedback('cancel', { tone: 'success', text: 'Payment method and active subscriptions canceled.' })
        fetchAll(email)
      },
    )
  }

  const subColumns: Column<AdminSubscription>[] = [
    { key: 'plan', label: 'Plan', align: 'left', sortValue: (r) => r.plan ?? '' },
    { key: 'status', label: 'Status', align: 'right', numeric: true, sortValue: (r) => r.status ?? 0 },
    { key: 'monthlyFee', label: 'Monthly fee', align: 'right', numeric: true, sortValue: (r) => r.monthlyFee ?? 0, format: (_v, r) => formatMoney(r.monthlyFee, r.currency) },
    { key: 'expiresAt', label: 'Expires', align: 'left', sortValue: (r) => r.expiresAt ?? '', format: (v) => formatDateTime(v as string) },
    { key: 'lastCanceledAt', label: 'Canceled', align: 'left', sortValue: (r) => r.lastCanceledAt ?? '', format: (v) => (v ? formatDateTime(v as string) : '—') },
  ]

  const txColumns: Column<AdminTransaction>[] = [
    { key: 'createdAt', label: 'Date', align: 'left', sortValue: (r) => r.createdAt ?? '', format: (v) => formatDateTime(v as string) },
    { key: 'cause', label: 'Cause', align: 'left', sortValue: (r) => r.cause ?? '' },
    { key: 'type', label: 'Type', align: 'left', sortValue: (r) => r.type ?? '' },
    { key: 'amount', label: 'Amount', align: 'right', numeric: true, sortValue: (r) => r.amount ?? 0, format: (_v, r) => formatMoney(r.amount, r.currency) },
    { key: 'result', label: 'Result', align: 'right', numeric: true, sortValue: (r) => r.result ?? 0 },
    { key: 'stripeId', label: 'Stripe id', align: 'left', sortValue: (r) => r.stripeId ?? '' },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user payment</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <AdminNotice feedback={loadFeedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to view their payment record.</p>}

        {email && (
          <>
            <div className="portal-actions">
              <ConfirmButton
                label="Cancel all payment for this user"
                pendingText="Drop payment method and cancel every active subscription?"
                disabled={isBusy('cancel')}
                onConfirm={cancelPayment}
              />
            </div>
            <AdminNotice feedback={feedbackFor('cancel')} />

            <AdminTabs
              tabs={[
                { id: 'subscriptions', label: 'Subscriptions' },
                { id: 'products', label: 'Products' },
                { id: 'transactions', label: 'Transactions' },
                { id: 'stripe', label: 'Stripe logs' },
              ]}
              active={tab}
              onChange={setTab}
            />

            {tab === 'subscriptions' &&
              (subscriptions === null ? (
                <p>Loading…</p>
              ) : (
                <div className="portal-table-scroll">
                  <Table columns={subColumns} rows={subscriptions} rowKey={(r, i) => `${r.plan}-${i}`} />
                </div>
              ))}

            {tab === 'products' &&
              (products === null ? (
                <p>Loading…</p>
              ) : products.length === 0 ? (
                <p className="portal-text-muted">No one-time products.</p>
              ) : (
                products.map((p) => (
                  <div className="portal-card" key={p.product}>
                    <h4>{p.productName}</h4>
                    <dl className="portal-kv">
                      <dt>Status</dt>
                      <dd>{p.status}</dd>
                      <dt>Price</dt>
                      <dd>{formatMoney(p.price, p.currency)}</dd>
                      <dt>Paid at</dt>
                      <dd>{p.payedAtLocal ?? 'not paid'}</dd>
                      <dt>Fail count</dt>
                      <dd>{p.failCount}</dd>
                    </dl>
                    <label htmlFor={`payedat-${p.product}`}>Mark paid at (local time, blank = now)</label>
                    <input
                      id={`payedat-${p.product}`}
                      type="datetime-local"
                      value={payedAtDrafts[p.product!] ?? ''}
                      onChange={(e) => setPayedAtDrafts((prev) => ({ ...prev, [p.product!]: e.target.value }))}
                    />
                    <label htmlFor={`altmethod-${p.product}`}>
                      <input
                        id={`altmethod-${p.product}`}
                        type="checkbox"
                        checked={altMethodDrafts[p.product!] ?? !!p.alternativeMethod}
                        onChange={(e) => setAltMethodDrafts((prev) => ({ ...prev, [p.product!]: e.target.checked }))}
                      />{' '}
                      Alternative payment method
                    </label>
                    <div className="portal-actions">
                      <button type="button" disabled={isBusy(`modify-${p.product}`)} onClick={() => markPaid(p)}>
                        Mark paid
                      </button>
                      <ConfirmButton
                        label="Delete product"
                        pendingText="Delete this product record?"
                        disabled={isBusy(`modify-${p.product}`)}
                        onConfirm={() => deleteProduct(p)}
                      />
                    </div>
                    <AdminNotice feedback={feedbackFor(`modify-${p.product}`)} />
                  </div>
                ))
              ))}

            {tab === 'transactions' &&
              (transactions === null ? (
                <p>Loading…</p>
              ) : (
                <div className="portal-table-scroll">
                  <Table columns={txColumns} rows={transactions} rowKey={(r) => String(r.id)} />
                </div>
              ))}

            {tab === 'stripe' && (
              <>
                {stripeItems.length === 0 ? (
                  <p className="portal-text-muted">No Stripe log entries.</p>
                ) : (
                  stripeItems.map((item, i) => (
                    <div className="portal-card" key={`${item.process}-${i}`}>
                      <dl className="portal-kv">
                        <dt>Date</dt>
                        <dd>{formatDateTime(item.createdAt)}</dd>
                        <dt>Type / method</dt>
                        <dd>
                          {item.type} / {item.method}
                        </dd>
                        <dt>Result</dt>
                        <dd>{item.result}</dd>
                      </dl>
                      <details>
                        <summary>Params</summary>
                        <pre>{redactedJson(item.params)}</pre>
                      </details>
                      <details>
                        <summary>Response</summary>
                        <pre>{redactedJson(item.response)}</pre>
                      </details>
                    </div>
                  ))
                )}
                {!stripeFinished && stripeCursor && (
                  <button type="button" onClick={loadMoreStripe}>
                    Load more
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
